import { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useApi } from '../providers/api-provider';
import { resetBrowserPreferences } from '../general/browser-preferences';

const actions = [
    { key: 'sessions', label: 'Clear all my sessions', description: 'Deletes your learning sessions and their events, resets lesson progress, and marks your assigned courses and lessons as new again.' },
    { key: 'forms', label: 'Clear all my filled forms', description: 'Deletes your form submissions and saved answers, so you can fill them in again.' },
    { key: 'agreements', label: 'Clear all my agreements', description: 'Deletes your Course Use agreements. You will be asked to accept them again when opening lessons.' },
    { key: 'feedback', label: 'Clear all my course feedback', description: 'Deletes your course ratings and reviews, including published reviews, so you can submit feedback again for completed courses.' },
    { key: 'preferences', label: 'Reset my browser preferences', description: 'Restores the default theme, pane sizes and AI language in this browser.' },
    { key: 'purchases', label: 'Clear all my test purchases', description: 'Deletes your sandbox purchase records and adjustments, and removes matching course access granted at checkout. Paddle sandbox transactions are retained.' },
] as const;

export default function AdminDebugPage() {
    const api = useApi();
    const [busy, setBusy] = useState<string | null>(null);
    const [notice, setNotice] = useState('');
    const [error, setError] = useState('');
    const [modules, setModules] = useState<{ module_key: string; name: string }[]>([]);
    const [moduleKey, setModuleKey] = useState<string | null>(null);
    const [modulesLoading, setModulesLoading] = useState(true);
    const [modulesError, setModulesError] = useState('');
    const [loadAttempt, setLoadAttempt] = useState(0);
    const saving = useRef(false);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => {
        if (!api) return;
        const controller = new AbortController();
        setModulesLoading(true); setModulesError('');
        api.getDebugModules(controller.signal).then(result => {
            if (!controller.signal.aborted) setModules(result);
        }).catch(() => {
            if (!controller.signal.aborted) setModulesError('Could not load modules. Please try again.');
        }).finally(() => { if (!controller.signal.aborted) setModulesLoading(false); });
        return () => controller.abort();
    }, [api, loadAttempt]);

    const runModuleAction = async (action: 'mark-not-complete' | 'sessions' | 'forms' | 'certificates' | 'reviews') => {
        if (!api || !moduleKey || saving.current) return;
        saving.current = true;
        setBusy(`module-${action}`); setNotice(''); setError('');
        const name = modules.find(module => module.module_key === moduleKey)?.name ?? 'Module';
        try {
            if (action === 'mark-not-complete') {
                await api.markMyDebugModuleNotComplete(moduleKey);
                if (mounted.current) setNotice(`${name} marked not complete for your account.`);
            } else {
                const deleted = await api.clearMyDebugModuleData(moduleKey, action);
                const count = Object.values(deleted).reduce((total, value) => total + value, 0);
                const summary = { sessions: 'sessions and events', forms: 'form submissions and saved answers', certificates: 'course certificates', reviews: 'course reviews' }[action];
                if (mounted.current) setNotice(`${name}: ${summary} deleted (${count} record${count === 1 ? '' : 's'}).`);
            }
        } catch {
            if (mounted.current) setError('Could not reset the selected module data. Please try again.');
        } finally {
            saving.current = false;
            if (mounted.current) setBusy(null);
        }
    };

    const clear = async (action: typeof actions[number]) => {
        if ((!api && action.key !== 'preferences') || saving.current) return;
        saving.current = true;
        setBusy(action.key); setNotice(''); setError('');
        try {
            if (action.key === 'preferences') {
                resetBrowserPreferences();
                if (mounted.current) setNotice('Browser preferences restored to defaults.');
                return;
            }
            const deleted = await api!.clearMyDebugData(action.key);
            if (action.key === 'purchases') sessionStorage.removeItem('course-to-buy');
            const count = Object.values(deleted).reduce((total, value) => total + value, 0);
            if (mounted.current) setNotice(`${action.label}: ${count} record${count === 1 ? '' : 's'} deleted.${action.key === 'sessions' ? ' New-item indicators reset.' : ''}`);
        } catch {
            if (mounted.current) setError('Could not clear your data. Please try again.');
        } finally {
            saving.current = false;
            if (mounted.current) setBusy(null);
        }
    };

    return (
        <section className="w-full" style={{ minHeight: 0, overflowY: 'auto' }}>
            <h1 className="page-title">Debug</h1>
            <p className="text-color-secondary">Reset your own data for testing. Deletions are permanent and apply to your signed-in account.</p>
            <div role="status" className="mb-3">{notice}</div>
            {error && <p role="alert" className="text-red-500">{error}</p>}
            <div className="flex flex-column gap-3">
                <div className="surface-card border-1 surface-border border-round p-3 flex flex-column gap-3">
                    <label htmlFor="debug-module">Module</label>
                    <div className="admin-toolbar flex flex-wrap align-items-center gap-3">
                        <Dropdown inputId="debug-module" value={moduleKey} options={modules} optionLabel="name" optionValue="module_key"
                            placeholder="Select a module" filter loading={modulesLoading} disabled={!api || modulesLoading || busy !== null}
                            onChange={event => setModuleKey(event.value)} className="w-full md:w-25rem" />
                        <Button label="Mark not complete" icon="pi pi-undo" outlined loading={busy === 'module-mark-not-complete'}
                            disabled={!api || !moduleKey || modulesLoading || busy !== null} onClick={() => runModuleAction('mark-not-complete')} />
                        <Button label="Delete sessions" icon="pi pi-trash" severity="danger" outlined loading={busy === 'module-sessions'}
                            disabled={!api || !moduleKey || modulesLoading || busy !== null} onClick={() => runModuleAction('sessions')} />
                        <Button label="Delete form submissions" icon="pi pi-trash" severity="danger" outlined loading={busy === 'module-forms'}
                            disabled={!api || !moduleKey || modulesLoading || busy !== null} onClick={() => runModuleAction('forms')} />
                        <Button label="Delete certificate" icon="pi pi-trash" severity="danger" outlined loading={busy === 'module-certificates'}
                            disabled={!api || !moduleKey || modulesLoading || busy !== null} onClick={() => runModuleAction('certificates')} />
                        <Button label="Delete review" icon="pi pi-trash" severity="danger" outlined loading={busy === 'module-reviews'}
                            disabled={!api || !moduleKey || modulesLoading || busy !== null} onClick={() => runModuleAction('reviews')} />
                    </div>
                    <p className="m-0 text-color-secondary">These actions apply only to the selected module for your account. Delete sessions removes its sessions and events. Delete form submissions removes saved answers and unlocks forms that can be filled in once. Delete Certificate and Delete Review remove your records for all courses containing the selected module. Certificates may be issued again if those courses remain complete; use Mark not complete first to test completion again.</p>
                    {modulesError && <div><p role="alert" className="text-red-500">{modulesError}</p>
                        <Button label="Reload modules" text disabled={busy !== null} onClick={() => setLoadAttempt(value => value + 1)} /></div>}
                </div>
                {actions.map(action => (
                    <div key={action.key} className="admin-toolbar surface-card border-1 surface-border border-round p-3 flex flex-wrap align-items-center justify-content-between gap-3">
                        <p className="m-0 text-color-secondary" style={{ flex: '1 1 20rem' }}>{action.description}</p>
                        <Button label={action.label} icon="pi pi-trash" severity="danger" outlined
                            loading={busy === action.key} disabled={(!api && action.key !== 'preferences') || busy !== null} onClick={() => clear(action)} />
                    </div>
                ))}
            </div>
        </section>
    );
}
